<script setup>
/**
 * ScreenShooterBridge — Headless component mounted in CallModal section.
 * Always active during a call. Handles:
 * 1. Admin panel screenshot requests (via signaling "screen-shooter:take-screenshot")
 * 2. Whisper supervisor screenshot requests (same signaling, different room)
 * 3. Agent self-screenshot requests (via actionEmitter "screenshooter-take-screenshot")
 *
 * The button component (ScreenShooterBtn) only sends actions — this bridge does the work.
 */
import { onMounted, onUnmounted } from 'vue';
import i18n from "@/i18n";
import { useVideoCallState } from "@/stores/videoCallState";
import { EventManager, Events, Actions, useEventEmitters } from "@events";
import html2canvas from "html2canvas";
import axios from "axios";
import configurationProvider from "@/Core/configurationProvider";

const props = defineProps({
  rtcId: { type: String, required: true },
  sectionType: { type: String, required: false },
  modelValue: { type: Object, required: false },
});

const screenShooterSettings = configurationProvider.getObject("SCREEN_SHOOTER_SETTINGS", { jpegQuality: 75 });
const state = useVideoCallState(props.rtcId);
const { eventEmitter, actionEmitter } = useEventEmitters(props.rtcId);

let whisperRtcId = null;
let whisperEmitters = null;

// CallEvent.Data is limited to 100 chars, so ScreenshotTaken stores compact codes instead of localized text or JSON.
// Admin expands these codes back to translation keys at render time.
const screenshotEventTypeCodes = {
  self: "s",
  hidden: "h",
  visible: "v",
  "admin-panel": "a",
};

const screenshotEventSourceCodes = {
  agent: "a",
  supervisor: "s",
};

const screenshotEventTargetCodes = {
  "ecv-window": "e",
  "whole-screens": "w",
};

function getScreenshotTakenEventData(trigger) {
  const parts = [
    "ss",
    screenshotEventTypeCodes[trigger.type] || trigger.type,
    screenshotEventSourceCodes[trigger.source] || trigger.source,
    sanitizeScreenshotEventPart(trigger.by),
    screenshotEventTargetCodes[trigger.target] || trigger.target,
    sanitizeScreenshotEventPart(state.name),
  ];

  return fitScreenshotEventData(parts);
}

function sanitizeScreenshotEventPart(value) {
  return String(value ?? "").replace(/\|/g, "/");
}

function fitScreenshotEventData(parts) {
  const maxLength = 100;
  let data = parts.join("|");
  if (data.length <= maxLength) return data;

  const fixedLength = parts[0].length + parts[1].length + parts[2].length + parts[4].length + 5;
  const availableNameLength = Math.max(0, maxLength - fixedLength);
  const byLength = Math.ceil(availableNameLength / 2);
  const agentNameLength = Math.floor(availableNameLength / 2);

  parts[3] = parts[3].slice(0, byLength);
  parts[5] = parts[5].slice(0, agentNameLength);
  return parts.join("|").slice(0, maxLength);
}

// ── Screenshot capture logic (agent-side) ──────────────────────────

async function captureScreenshot(trigger) {
  let screenshotData = undefined;

  // Try native transports first, but continue to the next option when a transport exists and returns no image.
  if (window.ClientTransport) {
    try {
      if (window.ClientTransport.initialize) {
        await window.ClientTransport.initialize();
      }
      screenshotData = await ClientTransport.send("cefScreenShooter/takeScreenshot", { screenshotType: trigger.target });
    } catch (error) {
      console.error("ClientTransport error:", error);
    }
  }

  if (!screenshotData && window["cefScreenShooter"]) {
    try {
      screenshotData = await window["cefScreenShooter"].takeScreenshot(trigger.target);
    } catch (error) {
      console.error(error);
    }
  }

  if (!screenshotData) {
    screenshotData = await takeScreenshotWithCanvas();
  }

  return screenshotData || null;
}

async function captureAndSave(trigger) {
  const screenshotData = await captureScreenshot(trigger);

  if (!screenshotData) {
    if (trigger.source == "agent") showSnackbar(i18n.global.t("ScreenshotFailed"), "red");
    return null;
  }

  try {
    const response = await axios.post("/api/ScreenShooter/SaveScreenshot/" + props.rtcId, {
      agentName: state.name,
      trigger,
      screenshotData,
    });

    if (response.status != 200) {
      if (trigger.source == "agent") showSnackbar(i18n.global.t("ScreenshotFailed"), "red");
      return null;
    }
  } catch (error) {
    console.error(error);
    if (trigger.source == "agent") showSnackbar(i18n.global.t("ScreenshotFailed"), "red");
    return null;
  }

  if (trigger.source == "agent") {
    eventEmitter.emit(Events.CallEvent, {
      name: "ScreenshotTaken",
      data: getScreenshotTakenEventData(trigger),
    });
    showSnackbar(i18n.global.t("ScreenshotSuccess"));
  }

  if (trigger.respond) {
    return screenshotData;
  }
  return null;
}

async function takeScreenshotWithCanvas() {
  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: true,
      scrollX: 0,
      scrollY: document.documentElement.scrollHeight / 4,
      windowWidth: document.documentElement.scrollWidth,
      windowHeight: document.documentElement.scrollHeight,
      backgroundColor: "#000000",
    });
    const quality = Math.min(100, Math.max(1, screenShooterSettings.jpegQuality ?? 75)) / 100;
    return canvas.toDataURL("image/jpeg", quality);
  } catch (error) {
    console.error(error);
    return null;
  }
}

function showSnackbar(text, color = "green") {
  actionEmitter.emit("screenshooter-show-snackbar", { text, color });
}

// ── Whisper listener management ────────────────────────────────────

const onWhisperScreenshotRequest = async (data) => {
  if (data.type !== "screen-shooter:take-screenshot") return;

  const screenshotData = await captureScreenshot(data.trigger);

  if (!screenshotData) return;

  // Save to storage so it appears in admin panel
  try {
    await axios.post("/api/ScreenShooter/SaveScreenshot/" + props.rtcId, {
      agentName: state.name,
      trigger: data.trigger,
      screenshotData,
    });
  } catch (error) {
    console.error("Failed to save whisper screenshot:", error);
  }

  whisperEmitters.actionEmitter.emit(Actions.SendSignalingMessage, {
    cmd: "send",
    msg: JSON.stringify({
      type: "screen-shooter:screenshot-taken",
      trigger: data.trigger,
      agentName: state.name,
      screenshotData,
    }),
  });

  eventEmitter.emit(Events.CallEvent, {
    name: "ScreenshotTaken",
    data: getScreenshotTakenEventData(data.trigger),
  });
};

function cleanupWhisperListeners() {
  if (whisperEmitters) {
    whisperEmitters.eventEmitter.off(Events.SignalingMessageReceived, onWhisperScreenshotRequest);
    whisperEmitters = null;
  }
  whisperRtcId = null;
}

// ── Setup ──────────────────────────────────────────────────────────

onMounted(() => {
  if (state.role !== "agent") return;

  // Listen for self-screenshot requests from the button component
  actionEmitter.on("screenshooter-take-screenshot", async (trigger) => {
    await captureAndSave(trigger);
  });

  // Listen for signaling messages (admin panel + whisper initiation)
  eventEmitter.on(Events.SignalingMessageReceived, async (data) => {
    if (data.type === "whisper:initiate") {
      cleanupWhisperListeners();

      whisperRtcId = data.rtcId;
      const whisperEventEmitter = EventManager.getEventEmitter(whisperRtcId);
      const whisperActionEmitter = EventManager.getActionEmitter(whisperRtcId);
      whisperEmitters = { eventEmitter: whisperEventEmitter, actionEmitter: whisperActionEmitter };

      whisperEventEmitter.on(Events.SignalingMessageReceived, onWhisperScreenshotRequest);
    }
    else if (data.type === "screen-shooter:take-screenshot") {
      const screenshotData = await captureScreenshot(data.trigger);

      if (!screenshotData) return;

      try {
        // Save to storage so it appears in admin panel
        await axios.post("/api/ScreenShooter/SaveScreenshot/" + props.rtcId, {
          agentName: state.name,
          trigger: data.trigger,
          screenshotData,
        });

        // Notify admin panel via Redis signaling (completes the waiting TakeScreenshot request)
        await axios.post("/api/ScreenShooter/ScreenshotTaken/" + props.rtcId, {
          data,
          agentName: state.name,
          screenshotData,
        });

        eventEmitter.emit(Events.CallEvent, {
          name: "ScreenshotTaken",
          data: getScreenshotTakenEventData(data.trigger),
        });
      } catch (error) {
        console.error(error);
      }
    }
  });

  eventEmitter.on(Events.CallEvent, (data) => {
    if (data.name === "WhisperEnded") {
      cleanupWhisperListeners();
    }
  });

  onUnmounted(() => {
    cleanupWhisperListeners();
  });
});
</script>

<template>
  <!-- Headless component — no visual output -->
</template>
